// ============================================================================
// Allocation card — one fulfilment leg (in-stock / transfer / backorder / dropship)
// with its item ledger and its Route timeline.
//
// Collapsed by default: the header carries just enough (PO#, shipment type,
// current Node → next Node, status pill + overdue / open-return indicators)
// for ops to decide whether to open the card. Cards NEVER auto-expand or
// auto-collapse on data refresh — the background tick and the Refresh button
// in sales_order_detail.tsx mutate live state, and self-opening cards would
// move under the operator's cursor. See CONTEXT.md → progressive disclosure.
//
// The one exception is `highlight`: the operator arrived from the PO board asking
// for THIS leg, so it starts expanded and scrolls itself into view. It is read once
// on mount only, so a later refresh still never moves the card.
// ============================================================================
import {
  CaretDownOutlined,
  CaretRightOutlined,
  ClockCircleFilled,
  EnvironmentOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import { Card, Descriptions, Space, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import {
  Allocation,
  AllocationItem,
  LogisticsDocument,
  OperationStage,
  ProductInfo,
  SalesOrderDetail,
  WarehouseAddress,
} from "../types";
import { fmtQtyUnit } from "../format";
import CopyText from "./copy_text";
import { AllocationActions } from "./escalation_actions";
import LogisticsDrawer from "./logistics_drawer";
import ProductThumb from "./product_thumb";
import { deriveRoute, Node as RouteNode } from "./route_derivation";
import StatusTag from "./status_tag";
import StageTable from "./stage_table";

// AllocationItem carries only `sales_order_line_id`, not the product — but the SO line it
// points at does. The product lookup is threaded in from the parent (which holds `so.lines`)
// so the row shows the product name, not just the line number, without a backend change.
const itemColumns = (
  lineProducts?: Map<number, ProductInfo | null>,
  lineUnits?: Map<number, string | null>
): ColumnsType<AllocationItem> => {
  const withUnit = (v: string | number | null | undefined, item: AllocationItem) =>
    fmtQtyUnit(v, lineUnits?.get(item.sales_order_line_id));
  return [
  { title: "SO Line", dataIndex: "sales_order_line_id", width: 90 },
  {
    // The hand-raised stock PO the allotment is sourced against, when the desk pointed it at one.
    // Hidden as a dash for ordinary stock / procured allotments so the ledger stays quiet.
    title: "Stock PO",
    key: "incoming_po_number",
    width: 110,
    render: (_, item) =>
      item.incoming_po_number ? (
        <Tag style={{ margin: 0 }}>{item.incoming_po_number}</Tag>
      ) : (
        <span style={{ color: "#ccc" }}>—</span>
      ),
  },
  {
    title: "Product",
    key: "product",
    render: (_, item) => {
      const product = lineProducts?.get(item.sales_order_line_id) ?? null;
      if (!product) return <span style={{ color: "#ccc" }}>—</span>;
      const name = product.product_name ?? "";
      const truncated = name.length > 50 ? `${name.slice(0, 50)}…` : name;
      return (
        <Space align="start">
          <ProductThumb src={product.image_url} />
          <div>
            <Tooltip title={name.length > 50 ? name : undefined}>
              <div>{truncated}</div>
            </Tooltip>
            {product.private_label_name && (
              <div style={{ fontSize: 11, color: "#666" }}>
                Private label: {product.private_label_name}
              </div>
            )}
            {product.sku && (
              <div style={{ fontSize: 11, color: "#999" }}>SKU {product.sku}</div>
            )}
          </div>
        </Space>
      );
    },
  },
  { title: "Qty", dataIndex: "qty", width: 90, render: withUnit },
  { title: "Received", dataIndex: "received_qty", width: 100, render: withUnit },
  { title: "Returned", dataIndex: "returned_qty", width: 100, render: withUnit },
  { title: "Net", dataIndex: "net_qty", width: 90, render: withUnit },
  { title: "Batch", dataIndex: "batch_no", render: (v) => v || "—" },
  ];
};

// The current → next Node pair for the header. Reads the derived Route so the
// header and the timeline cannot disagree about where the goods physically are.
// - If any Rail is in-flight (fill < 1): that Rail's from → to.
// - Otherwise every Rail has completed: current is the destination of the last Rail,
//   and there is no next Node to move to.
// - No Rails at all (empty / all-cancelled): render nothing.
const currentAndNext = (
  stages: OperationStage[]
): { current: RouteNode; next: RouteNode | null } | null => {
  const { rails } = deriveRoute(stages);
  if (rails.length === 0) return null;
  const inFlight = rails.find((r) => r.fill < 1);
  if (inFlight) return { current: inFlight.from, next: inFlight.to };
  return { current: rails[rails.length - 1].to, next: null };
};

// A non-terminal stage whose SLA has already elapsed. Reading `issue_state` (not
// `status`) so a "held" stage that is also overdue still counts — the wait itself
// is what has run over. Completed / cancelled stages leave the calculation.
const isOverdue = (stages: OperationStage[]): boolean => {
  const now = dayjs();
  return stages.some((s) => {
    if (!s.due_at) return false;
    if (s.issue_state === "completed" || s.issue_state === "cancelled") return false;
    return dayjs(s.due_at).isBefore(now);
  });
};

// A reverse-direction stage that has not yet completed — the return is still open.
// Rendered as its own indicator (not the main status pill) because the forward
// Route can be 100% complete while a return is still in progress: those are
// independent facts about the same allocation. See docs/adr/0001.
const hasOpenReturn = (stages: OperationStage[]): boolean =>
  stages.some(
    (s) => s.direction === "reverse" && s.issue_state !== "completed"
  );

// The one thing this leg is waiting on, surfaced on the collapsed header. The same fact
// already exists in StageChecklist, but two expands deep — and "what is holding this up"
// is the single question ops opens an order to answer.
const humanizeKey = (key: string) => {
  const spaced = key.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const blockingReason = (stages: OperationStage[]): string | null => {
  for (const stage of stages) {
    if (stage.issue_state === "completed" || stage.issue_state === "cancelled") continue;
    for (const step of stage.checklist ?? []) {
      if (step.state === "failed") return `Failed: ${humanizeKey(step.key)}`;
      if (step.state === "blocked" && step.blocking?.length) {
        return `Waiting on ${step.blocking.map(humanizeKey).join(", ").toLowerCase()}`;
      }
      if (step.state !== "done" && step.state !== "skipped") {
        return `Waiting on ${humanizeKey(step.key).toLowerCase()}`;
      }
    }
  }
  return null;
};

// A warehouse cell: its name, then the postal address and a maps link beneath (when we have them).
// Ops asked to see WHERE each leg's goods sit, not just the warehouse's name.
const WarehouseCell: React.FC<{
  name?: string | null;
  address?: WarehouseAddress | null;
}> = ({ name, address }) => (
  <div>
    <div>{name ?? "—"}</div>
    {address?.text && (
      <div style={{ fontSize: 11, color: "#999", lineHeight: 1.5 }}>{address.text}</div>
    )}
    {address?.maps_url && (
      <a href={address.maps_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11 }}>
        <EnvironmentOutlined /> Map
      </a>
    )}
  </div>
);

const AllocationCard: React.FC<{
  allocation: Allocation;
  onChanged?: (so: SalesOrderDetail) => void;
  // sales_order_line_id -> product, supplied by the parent from `so.lines` so item rows
  // can show the product name (AllocationItem itself does not carry it).
  lineProducts?: Map<number, ProductInfo | null>;
  // sales_order_line_id -> unit of measure, so the item ledger can show units on its qty columns.
  lineUnits?: Map<number, string | null>;
  // Deep-linked from /oms/po (?allocation=<id>): start open and scroll into view.
  highlight?: boolean;
}> = ({ allocation, onChanged, lineProducts, lineUnits, highlight }) => {
  const stages = [...allocation.stages].sort((a, b) => a.sequence - b.sequence);
  // Initial state only — never re-derived from `highlight`, so the card cannot move itself
  // after the operator has started working in it.
  const [expanded, setExpanded] = useState(!!highlight);
  const cardRef = useRef<HTMLDivElement>(null);
  const scrolled = useRef(false);
  const [drawer, setDrawer] = useState<{
    open: boolean;
    bookings: LogisticsDocument[];
    label: string;
  }>({ open: false, bookings: [], label: "" });

  // Once, on arrival from the PO board. Guarded by a ref so a re-render (refresh, an action
  // completing) never yanks the page back to this card.
  useEffect(() => {
    if (!highlight || scrolled.current || !cardRef.current) return;
    scrolled.current = true;
    cardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [highlight]);

  const cursor = currentAndNext(stages);
  const overdue = isOverdue(stages);
  const openReturn = hasOpenReturn(stages);
  const waitingOn = blockingReason(stages);

  return (
    <div ref={cardRef}>
      <Card
        size="small"
        title={
          <div
            data-testid="allocation-card-header"
            role="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
            style={{ cursor: "pointer", userSelect: "none" }}
          >
            <Space wrap>
              {expanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
              {allocation.po_number ? (
                <Tag color="purple" style={{ fontWeight: 600 }}>
                  <CopyText value={allocation.po_number} label="PO number">
                    PO# {allocation.po_number}
                  </CopyText>
                </Tag>
              ) : (
                <Tag>PO# —</Tag>
              )}
              <span>Allocation #{allocation.id}</span>
              <Tag color="geekblue">{allocation.shipment_type}</Tag>
              {cursor && (
                <span
                  data-testid="allocation-current-node"
                  style={{ color: "#595959", fontSize: 12 }}
                >
                  {cursor.current.label}
                  {cursor.next ? ` → ${cursor.next.label}` : ""}
                </span>
              )}
              <StatusTag
                status={allocation.state}
                tooltip={allocation.error_message || allocation.hold_reason}
                errorKind={allocation.error_kind}
              />
              {waitingOn && (
                <span
                  data-testid="allocation-waiting-on"
                  style={{ color: "#8c8c8c", fontSize: 12 }}
                >
                  {waitingOn}
                </span>
              )}
              {overdue && (
                <span data-testid="allocation-overdue">
                  <Tooltip title="An open stage is past its SLA due date">
                    <Tag
                      color="red"
                      icon={<ClockCircleFilled />}
                      style={{ fontWeight: 600 }}
                    >
                      overdue
                    </Tag>
                  </Tooltip>
                </span>
              )}
              {openReturn && (
                <span data-testid="allocation-open-return">
                  <Tooltip title="A return is still in progress on this allocation">
                    <Tag color="gold" icon={<RollbackOutlined />}>
                      open return
                    </Tag>
                  </Tooltip>
                </span>
              )}
              {onChanged && (
                <span onClick={(e) => e.stopPropagation()}>
                  <AllocationActions
                    allocation={allocation}
                    onDone={onChanged}
                    lineProducts={lineProducts}
                    lineUnits={lineUnits}
                  />
                </span>
              )}
            </Space>
          </div>
        }
        style={{
          marginBottom: 12,
          // The leg the operator asked for, outlined so it is obvious which of the order's
          // allotments the PO board sent them to.
          ...(highlight
            ? { borderColor: "#24AAB7", boxShadow: "0 0 0 2px rgba(36,170,183,0.15)" }
            : {}),
        }}
      >
        {expanded && (
          <div data-testid="allocation-card-body">
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Supplier">
                {allocation.supplier_name ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Supplier WH">
                <WarehouseCell
                  name={allocation.supplier_warehouse_name}
                  address={allocation.supplier_warehouse_address}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Receiving WH">
                <WarehouseCell
                  name={allocation.receiving_warehouse_name}
                  address={allocation.receiving_warehouse_address}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Fulfilment WH">
                <WarehouseCell
                  name={allocation.fulfilment_warehouse_name}
                  address={allocation.fulfilment_warehouse_address}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Vendor availability">
                <Tooltip title="The day the vendor has committed the goods will be ready. No leg is ever scheduled before it, and it survives re-plans — rescheduling a pickup moves this date.">
                  {allocation.promised_availability_date ?? "—"}
                </Tooltip>
              </Descriptions.Item>
              <Descriptions.Item label="Target delivery">
                <Tooltip title="The day the customer is promised the goods. Change it with Reschedule delivery.">
                  <span style={{ fontWeight: 600 }}>
                    {allocation.target_delivery_date ?? "—"}
                  </span>
                </Tooltip>
              </Descriptions.Item>
              <Descriptions.Item label="Batch">
                {allocation.batch_no ?? "—"}
              </Descriptions.Item>
            </Descriptions>

            <Table
              rowKey="id"
              size="small"
              pagination={false}
              columns={itemColumns(lineProducts, lineUnits)}
              dataSource={allocation.items}
              style={{ marginBottom: 12 }}
            />

            <div style={{ fontWeight: 600, marginBottom: 6 }}>Stages</div>
            <StageTable
              stages={stages}
              onOpenLogistics={(bookings, label) =>
                setDrawer({ open: true, bookings, label })
              }
              onChanged={onChanged}
            />

            <LogisticsDrawer
              open={drawer.open}
              title={drawer.label}
              bookings={drawer.bookings}
              onClose={() => setDrawer((d) => ({ ...d, open: false }))}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default AllocationCard;
